import { type MailLogEntry, useMailLogs } from "@features/mail/api";
import { PageLayoutWithHelp } from "@features/page-help";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { Card, Segmented, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

const statusOptions = [
  { label: "Все", value: "all" },
  { label: "Отправлено", value: "sent" },
  { label: "Ошибки", value: "failed" },
];

const statusTag = (status: string) => {
  const normalized = status?.toLowerCase();
  if (normalized === "failed") {
    return <Tag color="red">Ошибка</Tag>;
  }
  return <Tag color="green">Отправлено</Tag>;
};

const formatAttachments = (record: MailLogEntry) => {
  if (!record.attachments || record.attachments.length === 0) {
    return "—";
  }
  return record.attachments.map((att) => att.filename).join(", ");
};

const AdminMailLogPage = () => {
  const [status, setStatus] = useState<string>("all");
  const logsQuery = useMailLogs({
    status: status === "all" ? undefined : status,
    limit: 200,
  });
  const data = logsQuery.data ?? [];

  const columns = useMemo<ColumnsType<MailLogEntry>>(
    () => [
      {
        title: "Дата / Время",
        dataIndex: "created_at",
        width: 200,
        render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—"),
      },
      { title: "Получатель", dataIndex: "to", width: 220 },
      { title: "Тема", dataIndex: "subject", ellipsis: true },
      {
        title: "Статус",
        dataIndex: "status",
        width: 140,
        render: (value: string) => statusTag(value),
      },
      {
        title: "Ошибка",
        dataIndex: "error",
        ellipsis: true,
        width: 220,
        render: (value?: string) => value || "—",
      },
      {
        title: "Вложения",
        dataIndex: "attachments",
        width: 200,
        render: (_value, record) => formatAttachments(record),
      },
    ],
    [],
  );

  return (
    <PageLayoutWithHelp title="Отправленные письма">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Здесь отображаются все письма, отправленные через интерфейс AC Web. Хранятся только текст
          и список вложений (без файлов), чтобы можно было проверить историю и ошибки.
        </Typography.Text>

        <Card>
          <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            <Segmented
              options={statusOptions}
              value={status}
              onChange={(value) => setStatus(value as string)}
            />
            <EnterpriseTable<MailLogEntry>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={data}
              loading={logsQuery.isLoading}
              pagination={false}
              size="small"
              toolbar={{ reload: true, onReload: () => logsQuery.refetch() }}
              expandable={{
                expandedRowRender: (record) => (
                  <Space orientation="vertical" style={{ width: "100%" }}>
                    <Typography.Text type="secondary">Текст письма</Typography.Text>
                    <Card size="small">
                      <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                        {record.body || "—"}
                      </Typography.Paragraph>
                    </Card>
                    {record.attachments && record.attachments.length > 0 ? (
                      <Typography.Text type="secondary">
                        Вложения: {formatAttachments(record)}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ),
              }}
            />
          </Space>
        </Card>
      </Space>
    </PageLayoutWithHelp>
  );
};

export default AdminMailLogPage;
